/**
 * Shrinks a phone photo before upload: long edge at most 2048 px, JPEG at 0.85.
 * A 3-8 MB camera photo becomes roughly 300-600 KB, which matters on weak signal indoors.
 * The browser does the decoding, so iPhone HEIC photos become JPEG here too.
 * If anything fails, the original file is returned and the server handles it.
 */
const MAX_EDGE = 2048;
const QUALITY = 0.85;

async function decode(file: File): Promise<{ source: CanvasImageSource; width: number; height: number; done: () => void }> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { source: bitmap, width: bitmap.width, height: bitmap.height, done: () => bitmap.close() };
  } catch {
    // Older Safari: fall back to an <img>, which also applies EXIF orientation.
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, done: () => URL.revokeObjectURL(url) };
  }
}

export async function compressImage(file: File): Promise<Blob> {
  try {
    const { source, width, height, done } = await decode(file);
    try {
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
      return blob ?? file;
    } finally {
      done();
    }
  } catch {
    return file;
  }
}
